// src/components/ProviderScreenButton.jsx
// One-click conflict screening for a single provider, from their profile page.
// Runs a provider_scan on one NPI: the same report My Providers runs for a
// list or a selection, including the COI disclosure checks. (A vendor_scan,
// which the per-company "Screen" buttons run, does not include them.) The
// endpoint verifies Enterprise server-side, so this gate is presentation only.
import { useState } from "react";
import { useNavigate } from "react-router-dom";

function escapeHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export default function ProviderScreenButton({ npi, providerName, userId, fetchFn, isEnterprise, onLockedClick }) {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  async function handleScreen() {
    if (generating || !userId) return;
    // Must open synchronously, inside the click handler: opening a tab after
    // the await below would be blocked as a popup by most browsers.
    const reportWindow = window.open("", "_blank");
    const label = providerName && providerName !== "This provider" ? providerName : `NPI ${npi}`;
    if (reportWindow) {
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Generating report… — ProviderPayWatch</title>
            <style>
              body { background:#0a1628; color:#e2e8f0; font-family: system-ui, sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; margin:0; }
              .box { text-align:center; }
              .spinner { width:32px; height:32px; border:3px solid rgba(245,158,11,0.2); border-top-color:#f59e0b; border-radius:50%; animation:spin 0.8s linear infinite; margin:0 auto 16px; }
              @keyframes spin { to { transform: rotate(360deg); } }
              p { color:#94a3b8; font-size:14px; }
            </style>
          </head>
          <body>
            <div class="box">
              <div class="spinner"></div>
              <p>Screening ${escapeHtml(label)}…<br>This can take a moment.</p>
            </div>
          </body>
        </html>
      `);
      reportWindow.document.close();
    }
    setGenerating(true);
    setError(null);
    try {
      const res = await fetchFn("/api/generate-conflict-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, reportType: "provider_scan", npis: [npi] }),
      });
      const data = await res.json();
      if (!res.ok || data.status === "failed") {
        setError(data.error || "Report generation failed. Please try again.");
        if (reportWindow) reportWindow.close();
        return;
      }
      if (reportWindow) {
        reportWindow.location.href = `/conflict-report/${data.reportId}`;
      } else {
        navigate(`/conflict-report/${data.reportId}`);
      }
    } catch {
      setError("Network error. Please try again.");
      if (reportWindow) reportWindow.close();
    } finally {
      setGenerating(false);
    }
  }

  if (!isEnterprise) {
    return (
      <button
        onClick={onLockedClick}
        className="print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 bg-red-500/5 text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-all"
      >
        🔒 Screen provider <span className="text-[9px] px-1 py-0.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 uppercase tracking-wide">Enterprise</span>
      </button>
    );
  }

  return (
    <>
      <button
        onClick={handleScreen}
        disabled={generating}
        className="print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-all"
      >
        {generating ? "⏳ Screening…" : "🛡 Screen provider"}
      </button>
      {error && <p className="print:hidden basis-full text-red-400 text-xs">{error}</p>}
    </>
  );
}